import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, RightOutlined } from "@ant-design/icons";
import welcomeBg from "../../assets/homebg.png";
import mealApiService from "../../services/MealApiService";
import BottomNavBar from "../../components/BottomNavBar";
import { Card } from "../../components/ui/card";
import PredictedGlucoseSpikeCard from "../../components/HomePage/PredictedGlucoseSpikeCard";
import { MEAL_DETAIL_MACRO_CLASS } from "../../utils/nutritionMacroColors";

interface FoodItem {
  id: number | string;
  name: string;
  calorie_content_kcal: number;
  calories?: number; // Legacy field for compatibility
  fat_content_g: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  net_carbs_g: number;
  dietary_fiber_g: number;
  GI_estimate: number;
  GL_estimate: number;
  protein_content_g: number;
  image_url?: string;
  created_on?: string;
  created_at?: string;
  updated_at?: string;
  timestamp?: string;
  uploaded_at?: string;
  date_added?: string;
  [key: string]: any;
}

const MealDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const mealId = location.state?.mealId;
  const mealType = location.state?.mealType; // Meal type from Mealpage
  const mealName = location.state?.mealName; // Meal name from API
  const mealItems = useMemo(
    () => location.state?.mealItems ?? [],
    [location.state?.mealItems]
  );
  const mealImage = location.state?.mealImage;
  const mealDate = location.state?.mealDate;
  const fromMealPage = location.state?.fromMealPage;
  const ghiReasons: string[] = location.state?.ghiReasons ?? [];
  const bbsScore =
    location.state?.bbsScore ??
    (location.state?.glucoseSpikeData as { bbs_score?: number } | null)?.bbs_score ??
    null;
  const bbsLabel =
    location.state?.bbsLabel ??
    (location.state?.glucoseSpikeData as { bbs_label?: string } | null)?.bbs_label ??
    null;
  const bbsReasons: string[] = location.state?.bbsReasons ?? ghiReasons;
  const mealSource = location.state?.source; // 'barcode' for barcode scanned items
  const glucoseSpikeData = location.state?.glucoseSpikeData ?? null;

  const [foodItems, setFoodItems] = useState<FoodItem[]>(mealItems);
  const [filteredItems, setFilteredItems] = useState<FoodItem[]>(mealItems);

  const [apiTotals, setApiTotals] = useState<any>(location.state?.mealTotals || null);

  // Fetch meal data with React Query (only if not from MealPage)
  const { isLoading: loading } = useQuery({
    queryKey: ["mealItems", mealId],
    queryFn: async () => {
      if (fromMealPage && mealItems && mealItems.length > 0) {
        setFoodItems(mealItems);
        setFilteredItems(mealItems);
        return mealItems;
      }

      if (!mealId) {
        return [];
      }

      try {
        const response: any = await mealApiService.getFoodItemsByMealId(mealId);

        // If the response is an object containing results and totals
        if (response && response.results) {
          setFoodItems(response.results);
          setFilteredItems(response.results);
          if (response.totals) {
            setApiTotals(response.totals);
          }
          return response.results;
        }

        // Fallback for simple array response
        setFoodItems(response as any);
        setFilteredItems(response as any);
        return response;
      } catch (_error) {
        throw new Error("Failed to load meal items");
      }
    },
    enabled: !fromMealPage || (fromMealPage && mealItems.length === 0), // Only fetch if needed
  });
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Normalize API totals to handle different field name formats
  const normalizeApiTotals = (totals: any) => {
    if (!totals) return null;

    // If already in expected format, return as-is
    if (totals.calorie_content_kcal !== undefined) {
      return totals;
    }

    // Convert from barcode API format to expected format
    return {
      calorie_content_kcal: totals.calories || 0,
      protein_content_g: totals.protein || 0,
      carbohydrate_content_g: totals.carbohydrates || 0,
      fat_content_g: totals.fat || 0,
      sugar_g: totals.sugar || 0,
      dietary_fiber_g: totals.fiber || 0,
      saturated_fat_g: totals.saturated_fat || 0,
      trans_fat_g: totals.trans_fat || 0,
      net_carbs_g: (totals.carbohydrates || 0) - (totals.fiber || 0),
      sodium: totals.sodium || 0,
      cholesterol: totals.cholesterol || 0,
      alcohol: totals.alcohol || 0,
      caffeine: totals.caffeine || 0,
      GI_estimate: totals.GI_estimate || 0,
      GL_estimate: totals.GL_estimate || 0,
    };
  };

  // Use API totals strictly, no frontend calculation
  const displayTotals = normalizeApiTotals(apiTotals);

  useEffect(() => {
    setFilteredItems(foodItems);
  }, [foodItems]);

  const handleItemClick = (itemIndex: number) => {
    // If clicking on already expanded item, close it
    // If clicking on different item, close previous and open new one
    setExpandedItem(expandedItem === itemIndex ? null : itemIndex);
  };

  const formatTime = (item: FoodItem) => {
    const dateFields = [
      "uploaded_at",
      "created_at",
      "timestamp",
      "date_added",
      "updated_at",
      "created_on",
    ];

    // Find the first available date field
    for (const field of dateFields) {
      if (item[field] && item[field].toString().trim() !== "") {
        const rawTime = item[field].toString();
        return convertTo12HourFormat(rawTime);
      }
    }

    // If no date field found in item, try mealDate
    if (mealDate && mealDate.toString().trim() !== "") {
      return convertTo12HourFormat(mealDate.toString());
    }

    // Fallback to current time in 12-hour format
    return new Date().toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const convertTo12HourFormat = (dateTimeString: string) => {
    try {
      let timeString = "";

      // Extract time part from different formats
      if (dateTimeString.includes(" ") && dateTimeString.includes(":")) {
        timeString = dateTimeString.split(" ")[1]; // "2025-11-26 14:30:00" format
      } else if (dateTimeString.includes("T") && dateTimeString.includes(":")) {
        timeString = dateTimeString.split("T")[1].split("Z")[0]; // ISO format
      } else if (
        dateTimeString.includes(":") &&
        !dateTimeString.includes("-")
      ) {
        timeString = dateTimeString; // Already just time
      } else {
        // Parse as full date and extract time
        const date = new Date(dateTimeString);
        return date.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      }

      // Convert 24-hour to 12-hour format
      if (timeString.includes(":")) {
        const [hours, minutes] = timeString.split(":");
        const hour24 = parseInt(hours);
        const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
        const ampm = hour24 >= 12 ? "PM" : "AM";
        return `${hour12}:${minutes.padStart(2, "0")} ${ampm}`;
      }

      return dateTimeString;
    } catch (error) {
      return dateTimeString; // Return original if conversion fails
    }
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-3 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header Card with Image Overlay */}
        <div className="overflow-hidden border border-border bg-card shadow-lg">
          {/* Large Meal Image with Overlay Text */}
          {mealImage ? (
            <div className="relative h-64 w-full">
              {/* Background Image */}
              <img
                src={mealImage}
                alt="Meal"
                className="h-full w-full object-cover"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

              {/* Back Button */}
              <button
                onClick={() => navigate(-1)}
                className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-background/20 backdrop-blur-sm transition-colors hover:bg-background/40 dark:bg-white/20 dark:hover:bg-white/40"
              >
                <ArrowLeftOutlined className="text-title3 text-white" />
              </button>

              {/* Meal Info Overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-end justify-between">
                  <div className="min-w-0 flex-1">
                    <h1 className="mb-1 truncate text-title2 font-bold text-white">
                      {mealName || mealType || "Breakfast"}
                    </h1>
                    <p className="text-subhead text-white/90">
                      {filteredItems.length > 0 && formatTime(filteredItems[0])}
                    </p>
                  </div>
                  {bbsScore != null && (
                    <div className="text-right">
                      <div className="flex items-baseline justify-end">
                        <span
                          className={
                            bbsScore >= 80
                              ? "text-title2 font-bold text-emerald-400"
                              : bbsScore >= 50
                                ? "text-title2 font-bold text-amber-400"
                                : "text-title2 font-bold text-orange-400"
                          }
                        >
                          {Math.round(bbsScore)}
                        </span>
                        <span className="ml-1 text-caption1 text-white/70">/100 BBS</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Fallback Header without Image */
            <div className="flex items-center p-4">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-muted"
              >
                <ArrowLeftOutlined className="text-title3 text-foreground" />
              </button>
              <h1 className="min-w-0 flex-1 truncate text-title3 font-bold text-foreground">
                {mealName || mealType || "Breakfast"}
              </h1>
              <div className="text-right">
                <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.calories}`}>
                  {displayTotals
                    ? Math.round(displayTotals.calorie_content_kcal)
                    : 0}
                </p>
                <div className="text-footnote text-muted-foreground">kcal</div>
              </div>
            </div>
          )}
        </div>

        {/* BBS score card */}
        {filteredItems.length > 0 && bbsScore != null && (() => {
          const displayedScore = Math.round(bbsScore);
          const displayedLabel = bbsLabel;
          const scoreColor =
            displayedLabel === "Excellent" ? { ring: "ring-green-400", pulse: "bg-green-400", circleBorder: "border-green-300 bg-green-100 dark:border-green-700 dark:bg-green-900/40", text: "text-green-700 dark:text-green-400", badge: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400", bar: "bg-green-500", dot: "bg-green-500", card: "border-green-200/70 bg-gradient-to-br from-green-50 to-emerald-50/60 dark:border-green-800/40 dark:from-green-950/30 dark:to-emerald-950/20" }
            : displayedLabel === "Good" ? { ring: "ring-emerald-400", pulse: "bg-emerald-400", circleBorder: "border-emerald-300 bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-400", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400", bar: "bg-emerald-500", dot: "bg-emerald-500", card: "border-emerald-200/70 bg-gradient-to-br from-emerald-50 to-teal-50/60 dark:border-emerald-800/40 dark:from-emerald-950/30 dark:to-teal-950/20" }
            : displayedLabel === "Fair" ? { ring: "ring-yellow-400", pulse: "bg-yellow-400", circleBorder: "border-yellow-300 bg-yellow-100 dark:border-yellow-700 dark:bg-yellow-900/40", text: "text-yellow-700 dark:text-yellow-400", badge: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-500", bar: "bg-yellow-500", dot: "bg-yellow-500", card: "border-yellow-200/70 bg-gradient-to-br from-yellow-50 to-amber-50/60 dark:border-yellow-800/40 dark:from-yellow-950/30 dark:to-amber-950/20" }
            : { ring: "ring-red-400", pulse: "bg-red-400", circleBorder: "border-red-300 bg-red-100 dark:border-red-700 dark:bg-red-900/40", text: "text-red-700 dark:text-red-400", badge: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400", bar: "bg-red-500", dot: "bg-red-500", card: "border-red-200/70 bg-gradient-to-br from-red-50 to-rose-50/60 dark:border-red-800/40 dark:from-red-950/30 dark:to-rose-950/20" };

          return (
            <div className={`rounded-2xl border shadow-sm animate-[fadeSlideUp_0.5s_ease-out] ${scoreColor.card}`}>
              <div className="px-5 py-4">
                {/* Top row - Score circle + label + badge */}
                <div className="flex items-center gap-3">
                  {/* Animated Score circle with pulse ring */}
                  <div className="relative flex-shrink-0">
                    {/* Pulse ring */}
                    <div className={`absolute inset-0 rounded-full ${scoreColor.pulse} opacity-20 animate-ping`} />
                    <div className={`relative flex h-16 w-16 items-center justify-center rounded-full border-2 ring-2 ring-offset-2 ${scoreColor.circleBorder} ${scoreColor.ring} ring-offset-background`}>
                      <span className={`text-large-title font-black tabular-nums ${scoreColor.text}`}>
                        {displayedScore ?? "—"}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-caption2 font-semibold uppercase tracking-wider text-muted-foreground">Bite Balance Score</p>
                      <span className="shrink-0 text-footnote text-muted-foreground">out of 100</span>
                    </div>
                    {displayedLabel && (
                      <span className={`mt-1.5 inline-flex items-center rounded-full px-2.5 py-1 text-footnote font-bold whitespace-nowrap ${scoreColor.badge}`}>
                        {displayedLabel}
                      </span>
                    )}
                    {/* Progress bar below label */}
                    <div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-border/40">
                      <div
                        className={`h-full rounded-full ${scoreColor.bar}`}
                        style={{
                          width: `${Math.min(displayedScore ?? 0, 100)}%`,
                          animation: "expandWidth 1s ease-out forwards",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                {bbsReasons.length > 0 && (
                  <div className="my-3 border-t border-border/40" />
                )}

                {bbsReasons.length > 0 && (
                  <div className="space-y-1.5">
                    {bbsReasons.map((reason, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-2"
                        style={{ animation: `fadeSlideUp 0.4s ease-out ${0.3 + index * 0.1}s both` }}
                      >
                        <div className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${scoreColor.dot}`} />
                        <p className="text-footnote leading-relaxed text-muted-foreground">{reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Predicted Glucose Spike */}
        {filteredItems.length > 0 && glucoseSpikeData && (
          <PredictedGlucoseSpikeCard
            glucoseSpikeData={glucoseSpikeData}
            spikeValue={glucoseSpikeData?.predicted_spike_mg_dl}
            mealsLogged={1}
            variant="curve"
            showPredictedRange={true}
          />
        )}

        {/* Total Nutrition - Card style (same as ScannedFoodDetail) */}
        {displayTotals && (
          <>
            {/* Main Nutrition Card */}
            <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-headline font-bold text-foreground">Total Nutrition</h3>
                <span className="text-subhead text-muted-foreground">{mealName || mealType || "Meal"}</span>
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {/* Calories */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Calories</p>
                  <p className="text-title2 font-bold text-foreground">
                    {Math.round(displayTotals.calorie_content_kcal)}
                  </p>
                </div>

                {/* Protein */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Protein</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.protein}`}>
                    {Math.round(displayTotals.protein_content_g)}g
                  </p>
                </div>

                {/* Carbs */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Carbs</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.carbs}`}>
                    {Math.round(displayTotals.carbohydrate_content_g)}g
                  </p>
                </div>

                {/* Fat */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Fat</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.fat}`}>
                    {Math.round(displayTotals.fat_content_g)}g
                  </p>
                </div>

                {/* Sugar */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Sugar</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.sugar}`}>
                    {Math.round(displayTotals.sugar_g || 0)}g
                  </p>
                </div>

                {/* Fiber */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Fiber</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.fiber}`}>
                    {Math.round(displayTotals.dietary_fiber_g || 0)}g
                  </p>
                </div>

                {/* Saturated Fat */}
                <div>
                  <p className="text-subhead text-muted-foreground mb-1">Saturated Fat</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.satFat}`}>
                    {Math.round(displayTotals.saturated_fat_g || 0)}g
                  </p>
                </div>

                {/* Trans Fat */}
                 <div>
                  <p className="text-subhead text-muted-foreground mb-1">Net Carbs</p>
                  <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.netCarbs}`}>
                    {Math.round(displayTotals.net_carbs_g || 0)}g
                  </p>
                </div>

 {filteredItems.some(item => item.source !== "barcode") && (
                  <>
                    {/* GI */}
                    <div>
                      <p className="text-subhead text-muted-foreground mb-1">GI</p>
                      <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.glycemic}`}>
                        {Math.round(displayTotals.GI_estimate || 0)}
                      </p>
                    </div>

                    {/* GL */}
                    <div>
                      <p className="text-subhead text-muted-foreground mb-1">GL</p>
                      <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.glycemic}`}>
                        {Math.round(displayTotals.GL_estimate || 0)}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </>
        )}

        {/* Food Items Section - Hidden for barcode scanned items OR when no items */}
        {mealSource !== "barcode" && filteredItems.length > 0 && (
          <>
            {/* Food Items Heading */}
            <h3 className="px-1 text-headline font-bold text-foreground">Food Items</h3>

            {/* Food Items List */}
            <div className="space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
              <div className="text-foreground">Loading meal items...</div>
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg"
              >
                {/* Food Item Header */}
                <div
                  className="cursor-pointer p-4 transition-colors hover:bg-muted"
                  onClick={() => handleItemClick(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex flex-1 items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <h4 className="mb-1 break-words text-headline font-semibold text-foreground">{item.product_name || item.name}</h4>
                        {item.source === "barcode" && item.brand && (
                          <p className="break-words text-caption1 text-muted-foreground mb-1">{item.brand}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-footnote">
                          {item.source === "barcode" && (
                            <span className="inline-flex flex-shrink-0 items-center rounded-full bg-blue-100 px-2 py-0.5 text-caption2 font-medium text-blue-700">
                              Barcode
                            </span>
                          )}
                          <span className="flex-shrink-0 font-semibold text-blue-600">
                            {item.calorie_content_kcal} cal
                          </span>
                          <span className="flex-shrink-0 text-muted-foreground">•</span>
                          <span className="truncate text-muted-foreground">
                            {item.serving_description || `${item.serving_size_g}g`}
                          </span>
                        </div>
                      </div>
                    </div>
                    <RightOutlined
                      className={`text-muted-foreground transition-transform duration-200 ${expandedItem === index ? "rotate-90" : ""
                        }`}
                    />
                  </div>
                </div>

                {/* Accordion Content - Nutrition Facts */}
                {expandedItem === index && (
                  <div className="border-t border-border bg-muted p-4">
                    <div className="rounded-xl bg-card p-4 shadow-sm">
                      <h5 className="mb-3 text-subhead font-semibold text-foreground">
                        Nutrition Facts
                      </h5>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-callout text-muted-foreground">
                            Calories
                          </span>
                          <span className="text-callout font-medium text-foreground">
                            {item.calorie_content_kcal} Cal
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-callout text-muted-foreground">
                            Total Fats
                          </span>
                          <span className="text-callout font-medium text-foreground">
                            {item.fat_content_g}g
                          </span>
                        </div>
                        {item.trans_fat_g !== undefined && (
                          <div className="flex justify-between pl-4">
                            <span className="text-footnote text-muted-foreground">
                              Trans Fats
                            </span>
                            <span className="text-footnote text-foreground">
                              {item.trans_fat_g}g
                            </span>
                          </div>
                        )}
                        {item.saturated_fat_g !== undefined && (
                          <div className="flex justify-between pl-4">
                            <span className="text-footnote text-muted-foreground">
                              Saturated Fats
                            </span>
                            <span className="text-footnote text-foreground">
                              {item.saturated_fat_g}g
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-callout text-muted-foreground">
                            Cholesterol
                          </span>
                          <span className="text-callout font-medium text-foreground">
                            0g
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-callout text-muted-foreground">
                            Net Carbs
                          </span>
                          <span className="text-callout font-medium text-foreground">
                            {item.net_carbs_g}g
                          </span>
                        </div>
                        <div className="flex justify-between pl-4">
                          <span className="text-footnote text-muted-foreground">
                            Dietary Fibers
                          </span>
                          <span className="text-footnote text-foreground">
                            {item.dietary_fiber_g}g
                          </span>
                        </div>
                        {item.source !== "barcode" && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-callout text-muted-foreground">
                                Total GI
                              </span>
                              <span className="text-callout font-medium text-foreground">
                                {item.GI_estimate}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-callout text-muted-foreground">
                                Total GL
                              </span>
                              <span className="text-callout font-medium text-foreground">
                                {item.GL_estimate}
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="text-callout text-muted-foreground">
                            Proteins
                          </span>
                          <span className="text-callout font-medium text-foreground">
                            {item.protein_content_g}g
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="border border-border bg-card p-8 text-center shadow-lg">
              <div className="text-muted-foreground">
                No food items in this meal
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>

      <BottomNavBar activeIndex={1} />
    </div>
  );
};

export default MealDetailPage;
